/**
 * 통합 API 설정 관리
 * 모든 API 호출은 이 설정을 통해 이루어져야 함
 *
 * Web: Next.js rewrite 프록시 (/api/v1 → localhost:3000)
 * Capacitor: 직접 Express 서버 URL 사용
 */

/** Detect Capacitor native environment */
function isCapacitorNative(): boolean {
  if (typeof window === 'undefined') return false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const win = window as any;
  return !!(win.Capacitor?.isNativePlatform?.());
}

class ApiConfig {
  private static instance: ApiConfig;

  private constructor() {}

  public static getInstance(): ApiConfig {
    if (!ApiConfig.instance) {
      ApiConfig.instance = new ApiConfig();
    }
    return ApiConfig.instance;
  }

  /**
   * Check if running inside Capacitor native shell
   */
  public isNative(): boolean {
    return isCapacitorNative();
  }

  /**
   * 현재 환경에 맞는 API Base URL 반환
   */
  public getApiBaseUrl(): string {
    // Capacitor native: direct backend URL (no Next.js proxy)
    if (isCapacitorNative()) {
      return process.env.NEXT_PUBLIC_API_URL || 'https://refleco.app/api/v1';
    }
    // Web: Next.js rewrite 프록시를 통해 백엔드에 접근
    // next.config.ts의 rewrite 규칙이 /api/v1/* → localhost:3000/api/v1/* 으로 프록시
    return '/api/v1';
  }

  /**
   * AI 피드백 API URL 반환
   * Web: /api/ai-feedback (Next.js API route)
   * Capacitor: /api/v1/ai-feedback (Express backend)
   */
  public getAiFeedbackUrl(): string {
    if (isCapacitorNative()) {
      return `${this.getApiBaseUrl()}/ai-feedback`;
    }
    return '/api/ai-feedback';
  }

  /**
   * 완전한 API URL 생성
   */
  public getApiUrl(endpoint: string): string {
    const baseUrl = this.getApiBaseUrl();
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    return `${baseUrl}/${cleanEndpoint}`;
  }

  /**
   * 인증 헤더 포함한 fetch 옵션 생성
   */
  public getFetchOptions(options: RequestInit = {}): RequestInit {
    const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
    
    return {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
    };
  }

  /**
   * 통합 fetch 함수
   */
  public async fetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const url = this.getApiUrl(endpoint);
    const fetchOptions = this.getFetchOptions(options);
    
    return fetch(url, fetchOptions);
  }

  /**
   * 디버깅 정보 출력
   */
  public getDebugInfo(): object {
    if (typeof window === 'undefined') {
      return { environment: 'server' };
    }

    return {
      hostname: window.location.hostname,
      isLocalhost: window.location.hostname === 'localhost',
      apiBaseUrl: this.getApiBaseUrl(),
      hasToken: !!localStorage.getItem('accessToken'),
    };
  }
}

// 싱글톤 인스턴스 내보내기
export const apiConfig = ApiConfig.getInstance();
export default apiConfig;